/**
 * Help view (E13-F01) — the in-app user guide, registered as `renderView('help')`.
 *
 * Replaces the external password-protected page (aiaplace.com) with a guide that
 * lives in the same shell as the screens it documents, rewritten against the real
 * v2 button and screen names.
 *
 * ── Structure: one page, not a page per topic.
 * The approved structure keeps the original guide's shape — a single scrollable
 * article with a sticky table of contents. Reading a process end-to-end
 * ("incoming email → launched package") is the main job here, and a router-driven
 * topic-per-page would break that into 14 navigations.
 *
 * ── Search narrows the TOC, not the article.
 * See `HelpSearch` — the query filters topics (title + keywords), and the content
 * column always renders all 14 sections in order so scrolling never lands in a
 * hole. An empty result shows an explicit «No matching sections» state.
 *
 * ── Screenshots (E13-F01-T03).
 * The 12 illustrations are real frames of the deployed v2 UI, re-shot by
 * `scripts/capture-help-screenshots.mjs` and bundled from
 * `src/assets/help/<slug>.png` through `help-shots.ts`. Sections render them via
 * the `Shot` primitive, which returns `null` for a slug with no file — a section
 * whose frame has not been captured renders normally instead of leaving an empty
 * `<figure>` behind.
 */
import { useState } from 'react';
import type { AuthIdentity } from '@eer/shared';
import type { HelpPageId } from './help-sections.js';
import { filterHelpTopicsForPage } from './help-sections.js';
import { HelpToc } from './HelpToc.js';
import { Welcome } from './sections/Welcome.js';
import { Login } from './sections/Login.js';
import { MainScreen } from './sections/MainScreen.js';
import { Outlook } from './sections/Outlook.js';
import { Stages } from './sections/Stages.js';
import { Start } from './sections/Start.js';
import { Kickoff } from './sections/Kickoff.js';
import { Documents } from './sections/Documents.js';
import { Review } from './sections/Review.js';
import { Regenerate } from './sections/Regenerate.js';
import { Finish } from './sections/Finish.js';
import { Inbox } from './sections/Inbox.js';
import { Clients } from './sections/Clients.js';
import { Tips } from './sections/Tips.js';

export interface HelpViewProps {
  /** Authenticated principal — gates the Admin guide card (E13-F02). */
  readonly user: AuthIdentity | null;
  /** Open one of the other two routed guides (E13-F02/F03). */
  readonly onOpenGuide: (page: Exclude<HelpPageId, 'help'>) => void;
}

export function HelpView({ user, onOpenGuide }: HelpViewProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const topics = filterHelpTopicsForPage('help', query);
  const isAdmin = user?.role === 'admin';
  // Research has no access gate of its own today — every role can open `/research`,
  // so the guide is offered to everyone too (see decision-log.md, Help E13-F03).
  const canSeeResearch = true;

  return (
    <div
      data-testid="help-view"
      className="grid items-start gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]"
    >
      <HelpToc page="help" topics={topics} query={query} onQueryChange={setQuery} />

      {/* The guide itself — every section always rendered, in catalogue order. */}
      <article className="min-w-0 space-y-6">
        <Welcome />

        {isAdmin || canSeeResearch ? (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="help-other-guides">
            {isAdmin ? (
              <GuideCard
                testId="help-open-admin-guide"
                icon="fa-user-shield"
                title="Admin guide"
                description="Managing users, prompts & templates, and reference data."
                onClick={() => onOpenGuide('help_admin')}
              />
            ) : null}
            {canSeeResearch ? (
              <GuideCard
                testId="help-open-research-guide"
                icon="fa-flask"
                title="Research guide"
                description="Finding companies, running the AI chain, reviewing & publishing."
                onClick={() => onOpenGuide('help_research')}
              />
            ) : null}
          </div>
        ) : null}

        <Login />
        <MainScreen />
        <Outlook />
        <Stages />
        <Start />
        <Kickoff />
        <Documents />
        <Review />
        <Regenerate />
        <Finish />
        <Inbox />
        <Clients />
        <Tips />
      </article>
    </div>
  );
}

/** A link-out card to one of the other two routed guides (E13-F02/F03). */
function GuideCard({
  testId,
  icon,
  title,
  description,
  onClick,
}: {
  readonly testId: string;
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex items-start gap-3 rounded-lg border border-border bg-surface p-4 text-left transition hover:border-accent hover:bg-accent/5"
    >
      <i className={`fa-solid ${icon} mt-0.5 text-accent`} aria-hidden />
      <span>
        <span className="block text-sm font-semibold text-text-strong">{title}</span>
        <span className="mt-0.5 block text-[13px] leading-snug text-text-muted">{description}</span>
      </span>
    </button>
  );
}
